import {Trans} from '@lingui/macro';
import {isEqualWith} from 'lodash';
import React, {FC, ReactNode, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useNavigationBlocking} from '../../../srcCommon/app/navigationBlocking';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {PreferencesModel} from '../../models/api/preferencesModel';
import {SettingsPreferencesUrl} from '../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, SettingsTrackingTypesEnum, trackInterfaceEvent} from '../../services/tracking';
import {saveTeammatePreferencesAction} from '../../store/data/preferences/preferencesActions';
import {getPreferences} from '../../store/data/preferences/preferencesSelectors';
import {alphabetizeWorkspacesAsyncAction} from '../../store/data/workspace/alphabetizeWorkspaceActions';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {SettingsContentHeader} from '../common/settingsContentHeader';
import {SettingsDiscardChangesDialog} from '../common/settingsDiscardChangesDialog';
import {SettingsFieldLine} from '../common/settingsFieldLine';
import {
  SettingsFooter,
  StyledSettingsContent,
  StyledSettingsMain as SettingsMain,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {settingsHorizontalPaddingStyles} from '../settingsCommonConstants';
import {useTeammatePreferences} from './data/teammatePreferences';
import {checkIsValidPhoneNumberPrefix} from './items/teammateControls/defaultPhoneNumberPrefixInput';
import {PreferencesError} from './preferencesHelpers';
import {SettingsPreferencesHeader} from './settingsPreferencesHeader';

/*
 * Constants.
 */

export const preferencesMaxWidth = 650; // px

/*
 * Props.
 */

interface SettingsPreferencesListProps {
  panelId: SettingsPanelId;
  preferencesUrl: SettingsPreferencesUrl;
}

export interface SettingsPreferenceItemInheritableProps<T> {
  title?: ReactNode;
  description?: ReactNode;
  updatePreference: (updatedValue: T) => void;
}

/*
 * Style.
 */

const StyledSettingsMain = styled(SettingsMain)`
  ${settingsHorizontalPaddingStyles};
`;

// TODO: Should this be outside of StyledSettingsMain so we don't have to override the padding-left?
const StyledSettingsContentHeader = styled(SettingsContentHeader)`
  padding-left: 0;
`;

const StyledOuterDiv = styled.div`
  position: relative;
  height: 100%;
`;

const StyledInnerDiv = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: auto;
  /* Prevents the macOS overlay scrollbar from appearing behind the items on Safari/Chrome/Electron. */
  will-change: transform;
`;

const StyledListDiv = styled.div`
  position: absolute;
  width: 100%;
  max-width: ${preferencesMaxWidth}px;
`;

const StyledSettingsFieldLine = styled(SettingsFieldLine)`
  div:first-child > & {
    border-top: 0 none;
  }
`;

/*
 * Component.
 */

export const SettingsPreferencesList: FC<SettingsPreferencesListProps> = (props) => {
  const {panelId, preferencesUrl} = props;
  const dispatch = useAppDispatch();
  const {showBanner} = useSettingsBanners();

  const originalPreferences = useAppSelector(getPreferences);
  const [updatedPreferences, setUpdatedPreferences] = useState<Partial<PreferencesModel>>({});
  const preferences = useMemo<PreferencesModel>(
    () => ({...originalPreferences, ...updatedPreferences}),
    [originalPreferences, updatedPreferences],
  );

  const [searchQuery, setSearchQuery] = useState('');
  const teammatePreferencesArray = useTeammatePreferences(originalPreferences);
  const filteredTeammatePreferences = useMemo(() => {
    if (searchQuery.trim().length < 1) {
      return teammatePreferencesArray;
    }

    return teammatePreferencesArray.filter((pref) => {
      const searchText = `${pref.title} ${pref.description}`.toLowerCase();
      const lowerCaseSearchQuery = searchQuery.toLowerCase();

      return searchText.includes(lowerCaseSearchQuery);
    });
  }, [searchQuery, teammatePreferencesArray]);

  const canSave = useMemo(
    () =>
      !isPreferencesEqual(originalPreferences, preferences) &&
      checkIsValidPhoneNumberPrefix(preferences.defaultNumberPrefix),
    [originalPreferences, preferences],
  );

  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(panelId, canSave);

  useEffect(() => {
    const {initialPreference} = preferencesUrl;
    if (initialPreference) {
      setSearchQuery(initialPreference);
    }
  }, [preferencesUrl]);

  const patchPreferences = (updates: Partial<PreferencesModel>) => {
    setUpdatedPreferences((prevUpdates) => ({
      ...prevUpdates,
      ...updates,
    }));
  };

  const handleCancel = () => {
    setUpdatedPreferences({});
  };

  const handleSave = async () => {
    try {
      await dispatch(saveTeammatePreferencesAction(updatedPreferences));

      if ('shouldAlphabetizeWorkspace' in updatedPreferences) {
        trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_WORKSPACE_DISPLAY_REORDER, {
          section: null,
          settings_type: SettingsTrackingTypesEnum.PERSONAL,
          is_alphabetical: updatedPreferences.shouldAlphabetizeWorkspace,
        });
        // Alphabetize the user's workspaces (in their workspace resource) when toggling off this preference.
        if (updatedPreferences.shouldAlphabetizeWorkspace === false) {
          await dispatch(alphabetizeWorkspacesAsyncAction());
        }
      }

      showBanner({
        type: SettingsBannersEnum.SUCCESS,
        message: <Trans id="settings-teammate-prefs-save-success">Preferences updated</Trans>,
      });

      setUpdatedPreferences({});
    } catch (err) {
      if (err instanceof PreferencesError) {
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: err.message,
        });
      }
    }
  };

  const maybeRenderFooter = () => {
    if (!canSave) {
      return null;
    }

    return (
      <SettingsFooter>
        <Button isRounded variant={ButtonVariantsEnum.SECONDARY} onClick={handleCancel}>
          <Trans id="settings-form-cancel">Cancel</Trans>
        </Button>
        <Button isRounded variant={ButtonVariantsEnum.PRIMARY} onClick={handleSave}>
          <Trans id="settings-form-save">Save</Trans>
        </Button>
      </SettingsFooter>
    );
  };

  function maybeRenderDiscardChangesDialog() {
    if (!didBlockNavigation) {
      return null;
    }

    return <SettingsDiscardChangesDialog onCancel={cancelNavigation} onDiscard={unblockNavigation} />;
  }

  return (
    <StyledSettingsWrapper>
      <SettingsPreferencesHeader />
      <StyledSettingsMain>
        <StyledSettingsContent>
          <StyledSettingsContentHeader
            searchInputValue={searchQuery}
            onSearchInput={setSearchQuery}
            onExitClick={() => {
              setSearchQuery('');
            }}
          />
          <StyledOuterDiv>
            <StyledInnerDiv>
              <StyledListDiv>
                {filteredTeammatePreferences.map((preference) => (
                  <div key={`preference-item-${preference.title}`}>
                    {preference.overrideDefaultRender ? (
                      preference.renderControl({
                        preferences,
                        patchPreferences,
                        title: preference.title,
                        description: preference.description,
                      })
                    ) : (
                      <StyledSettingsFieldLine
                        title={preference.title}
                        subtitle={preference.description}
                        content={preference.renderControl({
                          preferences,
                          patchPreferences,
                          title: preference.title,
                          description: preference.description,
                        })}
                        renderTooltip={preference.renderTooltip}
                      />
                    )}
                  </div>
                ))}
              </StyledListDiv>
            </StyledInnerDiv>
          </StyledOuterDiv>
        </StyledSettingsContent>
        {maybeRenderDiscardChangesDialog()}
      </StyledSettingsMain>
      {maybeRenderFooter()}
    </StyledSettingsWrapper>
  );
};

/*
 * Helpers.
 */

function isPreferencesEqual(a: PreferencesModel, b: PreferencesModel): boolean {
  return isEqualWith(a, b, (_a, _b, key) => (key === 'updatedAt' ? true : undefined));
}
